import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const roadmapStages = [
  { 
    phase: "Phase 01", 
    title: "Planning & Orchestration", 
    desc: "Setting the foundation with powerful management and automation event tools.", 
    items: [ 
      { id: 'pilot', label: 'Pilot', detail: 'Central command for logistics.', icon: FiIcons.FiNavigation, color: 'indigo' }, 
      { id: 'nima', label: 'Nima', detail: 'Automate 1,000+ app workflows.', icon: FiIcons.FiActivity, color: 'cyan' } 
    ] 
  },
  { 
    phase: "Phase 02", 
    title: "Amplification & Reach", 
    desc: "Broadcasting your event across the social web to maximize visibility.", 
    items: [ 
      { id: 'unity', label: 'Unity', detail: 'Conversion-first event embedding.', icon: FiIcons.FiGift, color: 'emerald' }, 
      { id: 'bifrost', label: 'Bifrost', detail: '13-network social distribution.', icon: FiIcons.FiZap, color: 'pink' } 
    ] 
  },
  { 
    phase: "Phase 03", 
    title: "Immersion & Community", 
    desc: "Creating unforgettable experiences through AI, virtual worlds, and shared media.", 
    items: [ 
      { id: 'memories', label: 'Memories', detail: 'Private AI-powered social galleries.', icon: FiIcons.FiImage, color: 'purple' },
      { id: 'scotty', label: 'Scotty', detail: '24/7 AI guest concierge.', icon: FiIcons.FiCpu, color: 'violet' }, 
      { id: 'avatars', label: 'Avatars', detail: 'Immersive virtual venues.', icon: FiIcons.FiUsers, color: 'blue' } 
    ] 
  },
  { 
    phase: "Phase 04", 
    title: "Enterprise Scaling", 
    desc: "Powering global commerce and white-label brand authority.", 
    items: [ 
      { id: 'commerce', label: 'E-commerce', detail: '135+ currencies, 6 providers.', icon: FiIcons.FiShoppingCart, color: 'slate' }, 
      { id: 'whitelabel', label: 'Whitelabel', detail: 'Your domain, your brand.', icon: FiIcons.FiShield, color: 'slate' } 
    ] 
  }
];

const EcosystemFeatures = () => {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-2xl md:text-3xl font-bold text-indigo-600 tracking-[0.2em] uppercase mb-4">The Event Lifecycle of Success</h2>
        </div>
        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-100 -translate-x-1/2 hidden lg:block" />
          <div className="space-y-24">
            {roadmapStages.map((stage, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <div key={idx} className="relative">
                  <div className="absolute left-1/2 -translate-x-1/2 -top-12 z-10 hidden lg:block">
                    <div className="bg-white px-4 py-1.5 rounded-full border border-slate-200 shadow-sm">
                      <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600"> {stage.phase} </span>
                    </div>
                  </div>
                  <div className={`flex flex-col lg:flex-row items-center gap-12 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`w-full lg:w-1/2 ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                      <motion.div initial={{ opacity: 0, x: isEven ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4" >
                        <h3 className="text-3xl font-black text-slate-900 leading-tight">{stage.title}</h3>
                        <p className="text-slate-500 text-lg max-w-md mx-auto lg:mx-0">{stage.desc}</p>
                      </motion.div>
                    </div>
                    <div className="w-full lg:w-1/2">
                      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${!isEven ? 'lg:justify-start' : 'lg:justify-end'}`}>
                        {stage.items.map((item, i) => (
                          <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }} className="group p-5 bg-slate-50 border border-slate-100 rounded-3xl hover:bg-white hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-50/50 transition-all duration-500" >
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-2xl bg-white shadow-sm transition-all duration-500 group-hover:bg-indigo-600 group-hover:text-white`}>
                                <SafeIcon icon={item.icon} className="w-6 h-6" />
                              </div>
                              <div>
                                <h4 className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition-colors uppercase tracking-wider">{item.label}</h4>
                                <p className="text-slate-500 text-xs mt-1 leading-relaxed">{item.detail}</p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {idx < roadmapStages.length - 1 && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[-100px] h-20 w-px bg-gradient-to-b from-slate-200 to-transparent hidden lg:block" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EcosystemFeatures;